<!-- src/components/TextSetFilter.vue -->
<template>
  <div class="qset">
    <!-- search -->
    <div class="qset-search">
      <el-input
        v-model="q"
        size="mini"
        clearable
        placeholder="Search…"
        @keydown.stop
      />
    </div>

    <!-- values list (filtered) -->
    <div class="qset-list">
      <el-checkbox-group v-model="pending">
        <el-checkbox
          v-for="v in filteredOptions"
          :key="v === '' ? '__blank__' : v"
          :label="v"
          class="qset-item"
        >{{ fmt(v) }}</el-checkbox>
      </el-checkbox-group>
      <div v-if="!filteredOptions.length" class="qset-empty">No matches</div>
    </div>

    <div class="qset-footer">
      <span class="qset-link" @click="onConfirm">Confirm</span>
      <span class="qset-link" @click="onReset">Reset</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextSetFilter',
  data() {
    return {
      params: null,
      field: null,
      options: [],
      pending: [],
      applied: [],
      q: '' // search query
    }
  },
  computed: {
    filteredOptions() {
      if (!this.q) return this.options
      const needle = this.normalize(this.q)
      return this.options.filter(v => this.normalize(v).includes(needle))
    }
  },
  methods: {
    // normalize for matching: string, trimmed, case-insensitive
    normalize(v) {
      return (v == null ? '' : String(v)).trim().toLowerCase()
    },
    fmt(v) {
      return v === '' ? '(blank)' : String(v).toUpperCase()
    },
    resolveField() {
      const p = this.params || {}
      const f = (p.colDef && p.colDef.field) || (p.column && p.column.getColId())
      if (f) this.field = f
      return f
    },
    buildOptions() {
      if (!this.params || !this.params.api) return
      const p = this.params
      const field = this.resolveField()
      if (!field) return

      // custom valuesGetter support
      if (p.filterParams && typeof p.filterParams.valuesGetter === 'function') {
        let vals = p.filterParams.valuesGetter({ api: p.api, colDef: p.colDef, field }) || []
        const uniq = new Set(vals.map(v => (v == null ? '' : String(v))))
        this.options = Array.from(uniq).sort((a, b) => {
          if (a === '' && b !== '') return -1
          if (b === '' && a !== '') return 1
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
        })
        this.pending = this.pending.filter(v => uniq.has(v))
        return
      }

      // collect unique values after existing filters; if empty, fall back to all rows
      const uniq = Object.create(null)
      const pushVal = (node) => {
        if (!node || !node.data) return
        const raw = node.data[field]
        const s = (raw == null) ? '' : String(raw)
        uniq[s] = true
      }

      p.api.forEachNodeAfterFilter(pushVal)
      if (!Object.keys(uniq).length) p.api.forEachNode(pushVal)

      const opts = Object.keys(uniq)
      opts.sort((a, b) => {
        if (a === '' && b !== '') return -1
        if (b === '' && a !== '') return 1
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
      })
      this.options = opts
      this.pending = this.pending.filter(v => uniq[v] || uniq[String(v)])
    },

    // ag-Grid hooks
    init(params) { this.params = params; this.buildOptions() },
    afterGuiAttached() { this.buildOptions() },
    onNewRowsLoaded() { this.buildOptions() },

    isFilterActive() {
      return this.applied.length > 0
    },

    doesFilterPass(params) {
      const node = (params && params.node) ? params.node : params
      const f = this.field || this.resolveField()
      if (!f) return true

      const raw = node && node.data ? node.data[f] : null
      const v = (raw == null) ? '' : String(raw)
      if (!this.applied.length) return true
      return this.applied.includes(v)
    },

    getModel() {
      return this.applied.length
        ? { type: 'textSet', values: this.applied.slice() }
        : null
    },

    setModel(m) {
      const vals = (m && Array.isArray(m.values)) ? m.values.slice() : []
      this.applied = vals
      this.pending = vals.slice()
    },

    // ui actions
    onConfirm() {
      this.applied = this.pending.slice()
      this.params?.filterChangedCallback && this.params.filterChangedCallback()
    },

    onReset() {
      this.pending = []
      this.applied = []
      this.q = ''
      this.params?.filterChangedCallback && this.params.filterChangedCallback()
    }
  }
}
</script>

<style scoped>
.qset { min-width: 200px; padding: 6px; }
.qset-search { margin-bottom: 6px; }
.qset-list { max-height: 220px; overflow-y: auto; overflow-x: hidden; padding-right: 4px; }
.qset-item { display: block !important; margin: 4px 0; white-space: nowrap; }
.qset-empty { padding: 6px 2px; color: #999; font-size: 12px; }
.qset-footer { margin-top: 8px; display: flex; gap: 10px; font-size: 12px; color: #409EFF; user-select: none; }
.qset-link { cursor: pointer; }
.qset-link:hover { text-decoration: underline; }
</style>
